import { AddContextualData } from '@opendoor/observability/slim';
import faviconPath from '@opendoor/shared-fe/faviconPath';
import { DEPLOY_ENV } from 'components/globals';
import type {
  StaticOpenGraphFields,
  StaticPageProps,
  StaticSeoBaseFields,
} from 'components/static-site/staticPageProps';
import StaticSiteLayout from 'components/static-site/StaticSiteLayout';
import Head from 'next/head';

const getSeoBaseTags = (fields?: StaticSeoBaseFields) => {
  const title = fields?.title;
  const metaDescription = fields?.metaDescription;
  const canonicalUrl = fields?.canonicalUrl;

  const robots: Array<string> = [];
  if (fields?.index === false) {
    robots.push('noindex');
  }
  if (fields?.follow === false) {
    robots.push('nofollow');
  }

  return (
    <>
      <title key="title">{title ?? 'Opendoor | Sell your home the minute you’re ready.'}</title>
      <link rel="shortcut icon" href={faviconPath({ railsEnv: DEPLOY_ENV })} type="image/png" />
      {metaDescription ? (
        <meta key="description" name="description" content={metaDescription} />
      ) : null}
      {canonicalUrl ? <link key="canonical" rel="canonical" href={canonicalUrl} /> : null}
      {robots.length > 0 ? <meta key="robots" name="robots" content={robots.join(',')} /> : null}
    </>
  );
};

const getOpenGraphTags = (openGraphFields: StaticOpenGraphFields) => {
  const { url, type, title, description, image, siteName } = openGraphFields;

  return (
    <>
      <meta key="og:title" property="og:title" content={title} />
      <meta key="og:type" property="og:type" content={type} />
      <meta key="og:url" property="og:url" content={url} />
      <meta key="og:image" property="og:image" content={image} />
      <meta key="og:description" property="og:description" content={description} />
      <meta key="og:site_name" property="og:site_name" content={siteName} />
      <meta key="twitter:url" name="twitter:url" content={url} />
      <meta key="twitter:title" name="twitter:title" content={title} />
      {description && (
        <meta key="twitter:description" name="twitter:description" content={description} />
      )}
      <meta key="twitter:image" name="twitter:image" content={image} />
    </>
  );
};

type StaticPageLayoutProps = StaticPageProps & {
  hideFooter?: boolean;
  headerClassName?: string;
};

const StaticPageLayout = (props: StaticPageLayoutProps) => {
  const contextualData = { landingPageSlug: props.fields.slug };
  const seoBaseFields = props.fields.seoBase;
  const openGraphFields = props.fields.openGraph;

  return (
    <AddContextualData data={contextualData}>
      <Head>
        {getSeoBaseTags(seoBaseFields)}
        {openGraphFields ? getOpenGraphTags(openGraphFields) : null}
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      </Head>
      <StaticSiteLayout
        slug={`/${props.fields.slug}`}
        hideFooter={props.hideFooter}
        headerClassName={props.headerClassName}
      >
        {props.children}
      </StaticSiteLayout>
    </AddContextualData>
  );
};

export default StaticPageLayout;
